import { Link, useNavigate } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";
import { ImageIcon, Plus } from "@/components/icons";
import { Badge, Button, Skeleton, toast } from "@/components/kit";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { ReorderHintRow } from "@/modules/inventory/domain/types";
import { formatQtyWithUnit } from "@/modules/products/domain/unit-conversion";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { AppImage } from "@/shared/ui/AppImage";
import { inventoryApi } from "../api/client";
import { useInTransitSummaryQuery, useReorderHintsQuery } from "../api/queries";
import { saveImportOrderPhase13Prefill } from "../lib/import-prefill-phase13";
import { enrichImportLinesWithLastPrices } from "../lib/apply-last-import-prices";
import type { ImportOrderPhase13LineForm } from "../ui/ImportOrderPhase13Form";
import {
  InTransitSummaryCards,
  PlanningPackageIcon,
} from "./InTransitSummaryCards";
import { StockWithoutLotAlert } from "./StockWithoutLotAlert";

const CARRIER_LABELS: Record<string, string> = {
  hung_arun: "ຮຸ່ງອາລຸນ",
  anusith: "ອານຸສິດ",
  other: "ອື່ນໆ",
};

type InventoryPlanningPanelProps = {
  /** ຈຳກັດລາຍການໃນແຕ່ລະຄໍລຳ — ໃຊ້ໃນ dashboard */
  listLimit?: number;
  showHeading?: boolean;
};

export function InventoryPlanningPanel({
  listLimit = 5,
  showHeading = true,
}: InventoryPlanningPanelProps) {
  const nav = useNavigate();
  const canWrite = useActionPermission("inventory:write");
  const [bulkOrdering, setBulkOrdering] = useState(false);
  const inTransit = useInTransitSummaryQuery();
  const reorderHints = useReorderHintsQuery(listLimit);

  const isLoading = inTransit.isLoading || reorderHints.isLoading;
  const atCarrierRows = (inTransit.data?.atCarrierOrders ?? []).slice(
    0,
    listLimit,
  );
  const reorderRows = reorderHints.data?.data ?? [];
  const reorderTotal = reorderHints.data?.totalCount ?? 0;

  function hintToImportLine(row: ReorderHintRow): ImportOrderPhase13LineForm {
    return {
      variantId: row.variantId,
      productName: row.productName,
      variantName: row.variantName,
      imageKey: row.imageKey,
      description: "",
      orderedQuantity: Math.max(
        1,
        row.lowStockThreshold * 2 - row.stockQuantity,
      ),
      unitPriceForeign: 0,
    };
  }

  async function createImportFromHint(row: ReorderHintRow) {
    try {
      const lines = await enrichImportLinesWithLastPrices([hintToImportLine(row)]);
      saveImportOrderPhase13Prefill(lines);
      nav({ to: "/app/import-orders/new" });
    } catch {
      toast.error("ໂຫຼດລາຄາຄັ້ງກ່ອນບໍ່ສຳເລັດ");
    }
  }

  async function createImportFromAllHints() {
    setBulkOrdering(true);
    try {
      const result = await inventoryApi.getReorderHints(50);
      const lines = await enrichImportLinesWithLastPrices(
        result.data.map(hintToImportLine),
      );
      if (lines.length === 0) {
        toast.error("ບໍ່ມີລາຍການທີ່ຄວນສັ່ງ");
        return;
      }
      saveImportOrderPhase13Prefill(lines);
      nav({ to: "/app/import-orders/new" });
    } catch {
      toast.error("ໂຫຼດລາຍການບໍ່ສຳເລັດ");
    } finally {
      setBulkOrdering(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {showHeading ? (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="font-display text-lg">ວາງແຜນສັ່ງນຳເຂົ້າ</h2>
            <p className="text-muted-foreground text-sm">
              ຂອງລໍຖ້າທີ່ຂົນສົ່ງ ແລະ ສິນຄ້າທີ່ຄວນສັ່ງເພີ່ມ
            </p>
          </div>
          <Link
            to="/app/inventory/planning"
            className="shrink-0 text-secondary text-sm underline-offset-4 hover:underline"
          >
            ເປີດຕາຕະລາງວາງແຜນ →
          </Link>
        </div>
      ) : null}

      <InTransitSummaryCards
        summary={inTransit.data}
        reorderCount={reorderTotal}
        isLoading={isLoading}
      />

      <StockWithoutLotAlert limit={listLimit} />

      <div className="grid gap-4 lg:grid-cols-2">
        <PlanningListCard
          title="ຮອດຂົນສົ່ງແລ້ວ"
          emptyTitle="ບໍ່ມີໃບລໍຖ້າຮັບ"
          emptyDescription="ອັບເດດສະຖານະ «ຮອດຂົນສົ່ງ» ເມື່ອຂອງຮອດຮຸ່ງອາລຸນ"
          footerHref="/app/import-orders"
          footerLabel="ເບິ່ງສັ່ງນຳເຂົ້າທັງໝົດ"
          isLoading={inTransit.isLoading}
          isEmpty={atCarrierRows.length === 0}
        >
          {atCarrierRows.map((row) => (
            <button
              key={row.id}
              type="button"
              onClick={() =>
                nav({
                  to: "/app/import-orders/$id",
                  params: { id: row.id },
                })
              }
              className="flex w-full items-center gap-3 rounded-xl border border-border/60 bg-background/50 p-3 text-left transition-colors hover:border-secondary/40 hover:bg-muted/30"
            >
              <PlanningPackageIcon />
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate font-semibold text-sm tabular-nums">
                    {row.orderNumber}
                  </span>
                  <Badge variant="outline" className="shrink-0 tabular-nums">
                    {formatQtyWithUnit(row.totalPending, "ຊິ້ນ")}
                  </Badge>
                </div>
                <p className="truncate text-muted-foreground text-xs">
                  {row.carrier
                    ? (CARRIER_LABELS[row.carrier] ?? row.carrier)
                    : "—"}
                  {row.trackingInbound ? ` · ${row.trackingInbound}` : ""}
                </p>
                {row.expectedAt ? (
                  <p className="text-muted-foreground text-xs">
                    ຄາດຮອດ: {formatDateTimeLocal(row.expectedAt)}
                  </p>
                ) : null}
              </div>
            </button>
          ))}
        </PlanningListCard>

        <PlanningListCard
          title="ຄວນສັ່ງເພີ່ມ"
          emptyTitle="ສະຕັອກພໍໃຊ້"
          emptyDescription="ບໍ່ມີ variant ໃກ້ໝົດທີ່ຍັງບໍ່ມີຂອງລໍຖ້າ"
          footerHref="/app/inventory/planning"
          footerLabel="ເບິ່ງແຜນສັ່ງຂອງທັງໝົດ"
          isLoading={reorderHints.isLoading}
          isEmpty={reorderRows.length === 0}
          headerAction={
            canWrite ? (
              <Button
                variant="outline"
                size="sm"
                className="rounded-full"
                disabled={reorderTotal === 0 || bulkOrdering}
                isLoading={bulkOrdering}
                onClick={() => void createImportFromAllHints()}
              >
                <Plus data-icon="inline-start" />
                ສັ່ງໃໝ່
              </Button>
            ) : null
          }
        >
          {reorderRows.map((row) => (
            <div
              key={row.variantId}
              className="flex flex-col gap-3 rounded-xl border border-border/60 bg-background/50 p-3 sm:flex-row sm:items-center"
            >
              <div className="flex min-w-0 flex-1 items-center gap-3">
                {row.imageKey ? (
                  <AppImage
                    src={row.imageKey}
                    alt={row.productName}
                    className="size-10 shrink-0 rounded-lg object-cover"
                  />
                ) : (
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted/60 text-muted-foreground">
                    <ImageIcon className="size-5" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-sm">
                    {row.productName}
                  </p>
                  <p className="truncate text-muted-foreground text-xs">
                    {row.variantName}
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between gap-2 sm:shrink-0">
                <Badge
                  variant="outline"
                  className="shrink-0 border-destructive/30 bg-destructive/10 text-destructive tabular-nums"
                >
                  {formatQtyWithUnit(row.stockQuantity, "ຊິ້ນ")}
                </Badge>
                {canWrite ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="shrink-0 rounded-full"
                    onClick={() => createImportFromHint(row)}
                  >
                    <Plus data-icon="inline-start" />
                    ສັ່ງໃໝ່
                  </Button>
                ) : null}
              </div>
            </div>
          ))}
        </PlanningListCard>
      </div>
    </div>
  );
}

function PlanningListCard({
  title,
  emptyTitle,
  emptyDescription,
  footerHref,
  footerLabel,
  isLoading,
  isEmpty,
  headerAction,
  children,
}: {
  title: string;
  emptyTitle: string;
  emptyDescription: string;
  footerHref: string;
  footerLabel: string;
  isLoading?: boolean;
  isEmpty?: boolean;
  headerAction?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="brand-card flex flex-col gap-3 p-4 sm:p-5">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <h3 className="font-display text-base">{title}</h3>
        {headerAction ? (
          <div className="shrink-0 self-start sm:self-auto">{headerAction}</div>
        ) : null}
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-2">
          {["a", "b", "c"].map((id) => (
            <Skeleton key={id} className="h-16 rounded-xl" />
          ))}
        </div>
      ) : isEmpty ? (
        <div className="rounded-xl border border-border/80 border-dashed px-4 py-6 text-center">
          <p className="font-medium text-sm">{emptyTitle}</p>
          <p className="mt-1 text-muted-foreground text-xs">
            {emptyDescription}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">{children}</div>
      )}

      <Link
        to={footerHref}
        className="text-secondary text-sm underline-offset-4 hover:underline"
      >
        {footerLabel}
      </Link>
    </div>
  );
}
